import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import { useAuth } from '../contexts/AuthContext';
import usuarioService from '../services/UsuarioService';
import areaService from '../services/AreaService';
import { isGptClassicEnabled as hasGptClassicFeature } from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';

const AdminCreateAdminArea = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const stateRegionalId = location.state?.idRegional || null;
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(RegionalService.getFeatureFlags()), []);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);

  const [form, setForm] = useState({
    email: '',
    dni: '',
    nombre: '',
    apellido: '',
    telefono: '',
    legajo: '',
    idRegional: activeRegionalId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState('');
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);

  useEffect(() => {
    if (activeRegionalId && !form.idRegional) {
      setForm((prev) => ({ ...prev, idRegional: activeRegionalId }));
    }
  }, [activeRegionalId, form.idRegional]);

  useEffect(() => {
    const loadAreas = async () => {
      if (!activeRegionalId) {
        setAreas([]);
        setSelectedAreaIds([]);
        return;
      }

      setAreasLoading(true);
      setAreasError('');
      try {
        const response = await areaService.getAreasActivas(activeRegionalId);
        const nextAreas = Array.isArray(response) ? response : [];
        setAreas(nextAreas);
        setSelectedAreaIds((prev) =>
          prev.filter((id) => nextAreas.some((area) => (area.id || area.Id) === id))
        );
      } catch (error) {
        setAreas([]);
        setSelectedAreaIds([]);
        setAreasError(error.message || 'No pudimos cargar las areas activas del regional.');
      } finally {
        setAreasLoading(false);
      }
    };

    loadAreas();
  }, [activeRegionalId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAreaToggle = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId)
        ? prev.filter((id) => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    if (!form.dni.trim()) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }
    const legajoValue = (form.legajo || '').trim();
    if (!form.idRegional) {
      setMessage('No se pudo determinar el regional activo.');
      setMessageType('error');
      return;
    }
    if (selectedAreaIds.length === 0) {
      setMessage('Selecciona al menos un area para este admin.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando admin de area y asociando areas...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      const passwordToSend = form.dni.trim();

      await usuarioService.createAdminAreaUser({
        ...form,
        displayName,
        legajo: legajoValue || null,
        password: passwordToSend,
        idRegional: form.idRegional,
        areaIds: selectedAreaIds
      });
      const totalAreas = selectedAreaIds.length;
      setMessage(`Admin de area creado correctamente y asociado a ${totalAreas} ${totalAreas === 1 ? 'area' : 'areas'}.`);
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'resumen' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el admin de área.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            activeItemId="admin-area"
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de admin de área</h1>
            <p className="page-subtitle">Registra un nuevo usuario administrador de área.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-plus" />
                <span>Datos del admin de área</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Completa los datos requeridos. <strong>La contraseña inicial será el DNI</strong>; el usuario podrá cambiarla luego. El legajo es opcional y debes elegir al menos un area para dejar el acceso operativo.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="admin.area@ejemplo.com"
                  />
                </div>
                <div className="form-field">
                  <label>DNI</label>
                  <input
                    type="text"
                    value={form.dni}
                    onChange={(e) => handleChange('dni', e.target.value)}
                    placeholder="12345678"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Nombre"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Apellido"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                    <label>Legajo</label>
                    <input
                      type="text"
                      value={form.legajo}
                      onChange={(e) => handleChange('legajo', e.target.value)}
                      placeholder="C123"
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Areas a administrar</label>
                  <p style={{ margin: '0.35rem 0 0.75rem', color: '#64748b', fontSize: '0.95rem' }}>
                    El admin de area se va a crear ya vinculado a estas areas.
                  </p>
                  {areasLoading && <p style={{ margin: 0, color: '#475569' }}>Cargando areas activas...</p>}
                  {!areasLoading && areasError && <p style={{ margin: 0, color: '#b91c1c' }}>{areasError}</p>}
                  {!areasLoading && !areasError && areas.length === 0 && (
                    <p style={{ margin: 0, color: '#b45309' }}>
                      No hay areas activas en este regional. Primero crea o activa un area.
                    </p>
                  )}
                  {!areasLoading && !areasError && areas.length > 0 && (
                    <div style={{ display: 'grid', gap: '0.65rem' }}>
                      {areas.map((area) => {
                        const areaId = area.id || area.Id;
                        const nombre = area.nombre || area.Nombre || 'Area sin nombre';
                        const descripcion = area.descripcion || area.Descripcion || '';
                        const isSelected = selectedAreaIds.includes(areaId);

                        return (
                          <label
                            key={areaId}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.75rem',
                              padding: '0.85rem 1rem',
                              border: `1px solid ${isSelected ? '#3b82f6' : 'var(--gray-200)'}`,
                              borderRadius: '12px',
                              background: isSelected ? '#eff6ff' : '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleAreaToggle(areaId)}
                              style={{ marginTop: '0.2rem' }}
                            />
                            <span style={{ display: 'grid', gap: '0.2rem' }}>
                              <span style={{ fontWeight: 600, color: '#0f172a' }}>{nombre}</span>
                              {descripcion && (
                                <span style={{ color: '#64748b', fontSize: '0.92rem' }}>{descripcion}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'resumen' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting || areasLoading || areas.length === 0}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear admin de área</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminCreateAdminArea;
